'use client';

import React, { useState } from 'react';
import { Textarea } from '@/components/ui/textarea';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { ClipboardList, Trash2, Plus } from 'lucide-react';
import { EssayPlanItem, BodyParagraphType } from '@/types/writing';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface EssayPlanProps {
  plan: EssayPlanItem[];
  handlePlanTypeChange: (
    idx: number,
    value: BodyParagraphType | undefined
  ) => void;
  handlePlanContentChange: (idx: number, value: string) => void;
  addPlanItem: () => void;
  removePlanItem: (idx: number) => void;
  handleKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  isPlanOpen: boolean;
  onPlanOpenChange: (isOpen: boolean) => void;
}

const PLAN_TYPES: { value: BodyParagraphType; label: string }[] = [
  { value: 'one-idea', label: 'One Idea' },
  { value: 'two-idea', label: 'Two Ideas' },
  { value: 'counter-argument', label: 'Counter Arg' },
];

export function EssayPlan({
  plan,
  handlePlanTypeChange,
  handlePlanContentChange,
  addPlanItem,
  removePlanItem,
  handleKeyDown,
  isPlanOpen,
  onPlanOpenChange,
}: EssayPlanProps) {
  const [popoverOpenStates, setPopoverOpenStates] = useState<
    Record<number, boolean>
  >({});

  const handlePopoverOpenChange = (idx: number, open: boolean) => {
    setPopoverOpenStates((prev) => ({ ...prev, [idx]: open }));
  };

  return (
    <Accordion
      type='single'
      collapsible
      value={isPlanOpen ? 'plan' : ''}
      onValueChange={(value) => onPlanOpenChange(value === 'plan')}
      className='w-full border rounded-lg bg-muted'
    >
      <AccordionItem value='plan' className='border-b-0'>
        <AccordionTrigger className='text-sm font-semibold hover:no-underline py-3 px-4 rounded-t-lg hover:bg-accent focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none'>
          <div className='flex items-center gap-2'>
            <ClipboardList className='h-5 w-5 text-muted-foreground' />
            <span className='text-foreground'>Essay Plan</span>
          </div>
          <span className='text-xs font-normal text-muted-foreground mr-2 text-right flex-grow'>
            A good plan is key to good writing!
          </span>
        </AccordionTrigger>
        <AccordionContent className='p-4 bg-background rounded-b-lg'>
          <div className='space-y-4'>
            {plan.map((item, idx) => (
              <div key={idx} className='group/paragraph relative' tabIndex={-1}>
                <div className='absolute top-1 left-1 flex items-center gap-2 z-10 h-6'>
                  {item.type && item.label !== 'Position' && (
                    <span className='text-xs text-muted-foreground font-medium px-2'>
                      {PLAN_TYPES.find((t) => t.value === item.type)?.label ||
                        item.type}
                    </span>
                  )}

                  {item.label !== 'Position' && (
                    <div className='flex items-center gap-1 opacity-0 group-hover/paragraph:opacity-100 group-focus-within/paragraph:opacity-100 transition-opacity duration-150'>
                      {PLAN_TYPES.map((type) => (
                        <span
                          key={type.value}
                          onClick={() => handlePlanTypeChange(idx, type.value)}
                          className={cn(
                            'text-xs cursor-pointer px-2',
                            item.type === type.value
                              ? 'text-primary font-medium'
                              : 'text-muted-foreground hover:text-primary'
                          )}
                          role='button'
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              handlePlanTypeChange(idx, type.value);
                            }
                          }}
                        >
                          {type.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <Card className='relative bg-background/80 shadow-none border border-input p-0 transition-colors duration-150 hover:bg-muted/60 focus-within:bg-muted/60 rounded-md'>
                  <CardContent className='p-0 pt-6'>
                    <Textarea
                      id={`plan-content-${idx}`}
                      placeholder={
                        item.label === 'Position'
                          ? 'Position statement outline...'
                          : `Body paragraph #${idx} outline...`
                      }
                      value={item.content}
                      onChange={(e) =>
                        handlePlanContentChange(idx, e.target.value)
                      }
                      className='min-h-[60px] resize-none border-none bg-transparent focus:ring-0 focus:outline-none text-sm px-2 pb-2'
                      rows={2}
                      onKeyDown={handleKeyDown}
                    />
                    {idx === plan.length - 1 && item.label !== 'Position' && (
                      <div className='absolute bottom-2 right-2 opacity-0 group-hover/paragraph:opacity-100 group-focus-within/paragraph:opacity-100 transition-opacity duration-150'>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant='outline'
                              size='icon'
                              onClick={addPlanItem}
                              className='rounded-full h-6 w-6 shadow-sm opacity-80 hover:opacity-100 focus:opacity-100 transition-opacity duration-150 p-0'
                            >
                              <Plus className='h-4 w-4' />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Add Body Paragraph</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {item.label !== 'Position' && (
                  <Popover
                    open={popoverOpenStates[idx]}
                    onOpenChange={(open) => handlePopoverOpenChange(idx, open)}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant='ghost'
                        size='icon'
                        className='absolute top-1 right-1 h-6 w-6 opacity-0 group-hover/paragraph:opacity-100 group-focus-within/paragraph:opacity-100 transition-opacity duration-150 text-destructive z-10'
                      >
                        <Trash2 className='h-4 w-4' />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className='w-auto p-2'>
                      <div className='text-sm font-medium'>
                        Remove plan item?
                      </div>
                      <div className='flex gap-2 mt-2'>
                        <Button
                          variant='outline'
                          size='sm'
                          className='h-7'
                          onClick={() => handlePopoverOpenChange(idx, false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant='destructive'
                          size='sm'
                          className='h-7'
                          onClick={() => {
                            removePlanItem(idx);
                            handlePopoverOpenChange(idx, false);
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            ))}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
